import type { CSSProperties } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { Arena } from './Arena'
import { FighterModel } from './FighterModel'
import { arenas, fighters } from './content'
import { useFightStore } from './store'
import { GpuWarmup } from './GpuWarmup'
import { AdaptivePerformanceGovernor } from './AdaptivePerformanceGovernor'

const camTarget = new THREE.Vector3()
const lookTarget = new THREE.Vector3()

function SelectionCamera() {
  const { camera } = useThree()
  const player = useFightStore((s) => s.player)
  const enemy = useFightStore((s) => s.enemy)

  useFrame(({ clock }, dt) => {
    const t = clock.elapsedTime
    const centerX = (player.x + enemy.x) * 0.5
    const centerZ = (player.z + enemy.z) * 0.5
    const spread = Math.abs(player.x - enemy.x)
    const orbit = Math.sin(t * 0.18) * 0.11
    const distance = 8.7 + spread * 0.2
    camTarget.set(
      centerX + Math.sin(orbit) * distance * 0.58,
      2.55 + Math.sin(t * 0.42) * 0.045,
      centerZ + Math.cos(orbit) * distance,
    )
    camera.position.lerp(camTarget, 1 - Math.pow(0.001, dt))
    lookTarget.set(centerX, 1.18, centerZ)
    camera.lookAt(lookTarget)
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = THREE.MathUtils.damp(camera.fov, 37, 5, dt)
      camera.updateProjectionMatrix()
    }
  })
  return null
}

function SelectionRuntime() {
  const player = useFightStore((s) => s.player)
  const enemy = useFightStore((s) => s.enemy)
  const playerIndex = useFightStore((s) => s.playerIndex)
  const enemyIndex = useFightStore((s) => s.enemyIndex)
  const arenaIndex = useFightStore((s) => s.arenaIndex)
  const playerDef = fighters[playerIndex]
  const enemyDef = fighters[enemyIndex]
  const arena = arenas[arenaIndex]

  return (
    <>
      <Arena arena={arena} presentation="selection" />
      <FighterModel def={playerDef} state={player} side="left" presentation="selection" />
      <FighterModel def={enemyDef} state={enemy} side="right" presentation="selection" />
      <spotLight
        position={[-4.5, 6.5, 4]}
        target-position={[-2.5, 0.9, 0]}
        intensity={46}
        angle={0.34}
        penumbra={0.88}
        distance={14}
        color={playerDef.accent}
      />
      <spotLight
        position={[4.5, 6.5, 4]}
        target-position={[2.5, 0.9, 0]}
        intensity={46}
        angle={0.34}
        penumbra={0.88}
        distance={14}
        color={enemyDef.accent}
      />
      <SelectionCamera />
    </>
  )
}

export function SelectionVersusPlate(){
  const playerIndex=useFightStore(s=>s.playerIndex),enemyIndex=useFightStore(s=>s.enemyIndex),arenaIndex=useFightStore(s=>s.arenaIndex)
  const playerDef=fighters[playerIndex],enemyDef=fighters[enemyIndex],arena=arenas[arenaIndex]
  return <div className="selection-versus-plate" aria-hidden="true">
    <div style={{ '--accent': playerDef.accent } as CSSProperties}><span>P1</span><strong>{playerDef.name}</strong><small>{playerDef.title}</small></div>
    <b>VS</b>
    <div className="right" style={{ '--accent': enemyDef.accent } as CSSProperties}><span>{arena.name}</span><strong>{enemyDef.name}</strong><small>{enemyDef.title}</small></div>
  </div>
}

export function FighterSelectionStage3D({ onReady, readyToken }: { onReady?: () => void; readyToken?: number }) {
  const playerIndex = useFightStore((s) => s.playerIndex)
  const enemyIndex = useFightStore((s) => s.enemyIndex)
  const arenaIndex = useFightStore((s) => s.arenaIndex)
  const playerDef = fighters[playerIndex]
  const enemyDef = fighters[enemyIndex]
  const arena = arenas[arenaIndex]
  const readyKey = `${playerIndex}:${enemyIndex}:${arenaIndex}:${readyToken ?? 0}`

  return (
    <div className="fighter-selection-stage" aria-label="3D fighter selection stage">
      <Canvas
        shadows="soft"
        dpr={[1, 1.45]}
        camera={{ fov: 37, near: 0.1, far: 120, position: [0, 2.6, 10] }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.12,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        onCreated={({ gl }) => {
          gl.shadowMap.enabled = true
          gl.shadowMap.type = THREE.PCFSoftShadowMap
        }}
      >
        <GpuWarmup readyKey={readyKey} onReady={onReady} />
        <AdaptivePerformanceGovernor />
        <SelectionRuntime />
      </Canvas>
      <SelectionVersusPlate />
    </div>
  )
}



